// Loyalty redemption arithmetic.
//
// A redeemed point and the money it takes off the bill are two different
// numbers, even though most cinemas run them 1:1. Keeping the conversion here
// means the four call sites that build the `channel-26998e` payment channel
// (OrderPayment card/cash, cashChangeModal, paymentChannelsModal) all agree.
//
// Mode is chosen per cinema on the front end, matching the existing
// circuit-gating precedent (`isSilverbird` in OrderPayment.jsx). Any cinema not
// listed falls through to `direct`, which is exactly what shipped before this
// module existed.

export const REDEMPTION_MODES = {
  DIRECT: "direct",
  PERCENT: "percent",
};

// A point is worth 1% of the bill in percent mode, so 100 points clears it.
const PERCENT_MODE_MAX_POINTS = 100;

// Cinemas where 1 point buys 1% off the order total instead of 1 unit of
// currency. Add a cinemaId here to switch that cinema over.
export const PERCENT_REDEMPTION_CINEMA_IDS = ["sil-432f27aa"];

export const getRedemptionMode = (cinemaId) =>
  cinemaId && PERCENT_REDEMPTION_CINEMA_IDS.includes(cinemaId)
    ? REDEMPTION_MODES.PERCENT
    : REDEMPTION_MODES.DIRECT;

const toMoney = (num) => Math.round((Number(num) || 0) * 100) / 100;

/**
 * Parse the on-screen keyboard's raw string into a point count.
 * Returns NaN for anything that isn't a whole, non-negative number — the
 * keyboard is numeric but the field is a plain text input, so "1e3", "+5",
 * " 12 " and "1.5" are all reachable.
 */
export const normalisePoints = (raw) => {
  if (raw === null || raw === undefined) return NaN;

  const trimmed = String(raw).trim();
  if (!/^\d+$/.test(trimmed)) return NaN;

  return Number(trimmed);
};

/** Money that `points` takes off a bill of `orderTotal`. */
export const pointsToCurrency = (points, orderTotal, cinemaId) => {
  const count = Number(points) || 0;

  if (getRedemptionMode(cinemaId) === REDEMPTION_MODES.PERCENT) {
    return toMoney(((Number(orderTotal) || 0) * count) / 100);
  }

  return toMoney(count);
};

/** Most points the member can usefully spend on this basket. */
export const maxRedeemablePoints = (balance, orderTotal, cinemaId) => {
  const available = Math.max(0, Math.floor(Number(balance) || 0));

  if (getRedemptionMode(cinemaId) === REDEMPTION_MODES.PERCENT) {
    return Math.min(available, PERCENT_MODE_MAX_POINTS);
  }

  return Math.min(available, Math.floor(Number(orderTotal) || 0));
};

/**
 * Validate a redemption attempt.
 *
 * Returns `{ errorKey, points, currency }`. `errorKey` is an i18n key or null;
 * the caller runs it through t(). An empty input is valid and means "redeem
 * nothing" — that is how the flow is skipped when loyalty is enforced.
 */
export const validateRedemption = ({ raw, balance, orderTotal, cinemaId }) => {
  const empty = { errorKey: null, points: 0, currency: 0 };

  if (raw === null || raw === undefined || String(raw).trim() === "") {
    return empty;
  }

  const points = normalisePoints(raw);
  const available = Number(balance) || 0;

  if (Number.isNaN(points)) {
    return {
      errorKey: "OrderPayment:paymentchannel_modal.errors.points.invalid",
      points: 0,
      currency: 0,
    };
  }

  if (points === 0) return empty;

  if (available <= 0) {
    return {
      errorKey: "OrderPayment:paymentchannel_modal.errors.points.a",
      points,
      currency: 0,
    };
  }

  if (points > available) {
    return {
      errorKey: "OrderPayment:paymentchannel_modal.errors.points.b",
      points,
      currency: 0,
    };
  }

  const currency = pointsToCurrency(points, orderTotal, cinemaId);

  if (currency > (Number(orderTotal) || 0)) {
    return {
      errorKey: "OrderPayment:paymentchannel_modal.errors.points.c",
      points,
      currency,
    };
  }

  return { errorKey: null, points, currency };
};
